import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';

// The single source of runtime theme truth (SD-054 §C.1). Holds the user's
// PREFERENCE (light | dark | system) and the RESOLVED theme (light | dark),
// stamps the root theme attribute SD-052 established on `<html>`, persists the
// explicit choice, and — while on `system` — follows `prefers-color-scheme`.
//
// The resolve/apply/read helpers are framework-free and exported so the
// no-flash bootstrap (ticket 05) can stamp the correct attribute BEFORE the app
// (and thus before Vue) mounts, reusing the *same* implementation — one source
// of truth for "which theme + how to stamp it".

// Preference enum — CODE_STYLE §5: an `as const` array + derived union + `is…`
// guard, never a TS `enum` (mirrors @cosmic-safari/shared/enums).
export const THEME_PREFERENCE = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCE)[number];
export const isThemePreference = (value: string): value is ThemePreference =>
  (THEME_PREFERENCE as readonly string[]).includes(value);

// The concrete theme actually applied to the DOM — `system` is never stamped.
export type ResolvedTheme = 'light' | 'dark';

// The root attribute + its query/key, defined once. `data-theme` on `<html>` is
// the SD-052 mechanism (styles/index.css: `:root[data-theme='dark']`); absence
// resolves to light.
const THEME_ATTRIBUTE = 'data-theme';
const STORAGE_KEY = 'hubl.theme';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

// --- Pure helpers (no Vue import): shared with the bootstrap step ------------

/** Resolve a preference to a concrete theme; `system` follows the OS flag. */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

/** Stamp the resolved theme onto the root element — the only DOM write. */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute(THEME_ATTRIBUTE, resolved);
}

/** Read the persisted preference; `system` when absent, invalid, or unreadable. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null && isThemePreference(stored) ? stored : 'system';
  } catch {
    // localStorage can throw in private-mode / sandboxed contexts — fall back.
    return 'system';
  }
}

// --- Composable --------------------------------------------------------------

export interface UseTheme {
  /** The user's stored intent: light | dark | system. */
  readonly preference: Ref<ThemePreference>;
  /** The concrete theme currently applied (system → light|dark by OS). */
  readonly resolved: ComputedRef<ResolvedTheme>;
  /** Set + persist the preference and re-stamp the root attribute. */
  readonly setPreference: (preference: ThemePreference) => void;
}

export function useTheme(): UseTheme {
  const preference = ref<ThemePreference>(readStoredPreference());

  // Reactive `prefers-color-scheme` signal; `resolved` gates on it so an OS flip
  // only re-stamps while the preference is `system`.
  const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY);
  const systemPrefersDark = ref(mediaQuery.matches);
  const onSystemChange = (event: MediaQueryListEvent): void => {
    systemPrefersDark.value = event.matches;
  };
  mediaQuery.addEventListener('change', onSystemChange);

  const resolved = computed<ResolvedTheme>(() =>
    resolveTheme(preference.value, systemPrefersDark.value)
  );

  // Re-stamp whenever the resolved theme changes (explicit set OR an OS flip
  // while on `system`). `flush: 'sync'` applies the attribute the instant the
  // theme resolves rather than deferring to the post-render tick, so an OS flip
  // never shows a frame of the wrong theme. The bootstrap already stamped the
  // correct value, so the immediate run writes the same attribute — no flash.
  watch(resolved, (theme) => applyTheme(theme), { immediate: true, flush: 'sync' });

  function setPreference(next: ThemePreference): void {
    preference.value = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence best-effort; the in-memory choice still applies this session.
    }
    applyTheme(resolved.value);
  }

  onScopeDispose(() => {
    mediaQuery.removeEventListener('change', onSystemChange);
  });

  return { preference, resolved, setPreference };
}
